import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Zap, AlertTriangle, Star } from 'lucide-react';

// Timing result types
const TIMING_RESULTS = {
  PERFECT: { text: 'PERFECT!', color: '#FFD700', multiplier: 1.5, bonus: 2 },
  GOOD: { text: 'GOOD!', color: '#00FF99', multiplier: 1.2, bonus: 1 },
  OKAY: { text: 'OK', color: '#00CCFF', multiplier: 1.0, bonus: 0 },
  MISS: { text: 'MISS!', color: '#FF0066', multiplier: 0.5, bonus: -1 },
};

/**
 * Anti-Cheat Precision Timing Skill Bar Component
 * 
 * Features:
 * - Moving cursor across the bar
 * - Randomized "Green Zone" position
 * - News sentiment affects zone size
 * - Visual feedback for timing accuracy
 * - Haptic feedback on mobile
 * - Anti-bot variable speed
 */
const SkillBar = ({
  onComplete,
  sentimentModifier = 0, // -2 to +2, affects green zone size
  cardPower = 3,
  cardClass = 'meme',
  disabled = false,
  autoTriggerDelay = null, // Auto-click after delay (for testing/AI)
}) => {
  // State
  const [phase, setPhase] = useState('ready'); // ready, active, result
  const [cursorPosition, setCursorPosition] = useState(0);
  const [greenZone, setGreenZone] = useState({ start: 40, end: 60 }); // Percentage positions
  const [perfectZone, setPerfectZone] = useState({ start: 47, end: 53 }); // Inner perfect zone
  const [result, setResult] = useState(null);
  const [isMovingRight, setIsMovingRight] = useState(true);
  const [speed, setSpeed] = useState(1);
  
  // Refs
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasClickedRef = useRef(false);
  
  // Calculate zone sizes based on sentiment
  const calculateZones = useCallback(() => {
    // Base green zone size: 20% of bar
    // Sentiment can modify by ±8%
    const baseSize = 20;
    const sentimentBonus = sentimentModifier * 4; // -8 to +8
    const greenZoneSize = Math.max(10, Math.min(35, baseSize + sentimentBonus));
    
    // Perfect zone is always 30% of green zone
    const perfectZoneSize = greenZoneSize * 0.3;
    
    // Randomize position (ensure it fits within bounds)
    const maxStart = 100 - greenZoneSize - 5;
    const minStart = 5;
    const randomStart = minStart + Math.random() * (maxStart - minStart);
    
    const greenStart = randomStart;
    const greenEnd = randomStart + greenZoneSize;
    
    // Perfect zone centered within green zone
    const perfectStart = greenStart + (greenZoneSize - perfectZoneSize) / 2;
    const perfectEnd = perfectStart + perfectZoneSize;
    
    setGreenZone({ start: greenStart, end: greenEnd });
    setPerfectZone({ start: perfectStart, end: perfectEnd });
  }, [sentimentModifier]);
  
  // Initialize zones when component mounts or sentiment changes
  useEffect(() => {
    calculateZones();
  }, [calculateZones]);
  
  // Variable speed to prevent bots (changes randomly)
  useEffect(() => {
    if (phase !== 'active') return;
    
    const speedInterval = setInterval(() => {
      // Random speed between 0.8 and 1.3
      const newSpeed = 0.8 + Math.random() * 0.5;
      setSpeed(newSpeed);
    }, 300 + Math.random() * 200);
    
    return () => clearInterval(speedInterval);
  }, [phase]);
  
  // Cursor animation loop
  useEffect(() => {
    if (phase !== 'active' || disabled) return;
    
    let lastTime = performance.now();
    
    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      // Speed: complete bar in ~1.5s at base speed
      const baseSpeed = 100 / 1500; // percent per ms
      const adjustedSpeed = baseSpeed * speed * deltaTime;
      
      setCursorPosition(prev => {
        let newPos = prev;
        
        if (isMovingRight) {
          newPos = prev + adjustedSpeed;
          if (newPos >= 100) {
            newPos = 100;
            setIsMovingRight(false);
          }
        } else {
          newPos = prev - adjustedSpeed;
          if (newPos <= 0) {
            newPos = 0;
            setIsMovingRight(true);
          }
        }
        
        return newPos;
      });
      
      if (phase === 'active') {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [phase, speed, isMovingRight, disabled]);
  
  // Auto-trigger for AI/testing
  useEffect(() => {
    if (autoTriggerDelay && phase === 'active') {
      const timer = setTimeout(() => {
        if (!hasClickedRef.current) {
          handleClick();
        }
      }, autoTriggerDelay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTriggerDelay, phase]);
  
  // Start the skill bar
  const handleStart = useCallback(() => {
    if (phase !== 'ready' || disabled) return;
    
    setCursorPosition(0);
    setIsMovingRight(true);
    hasClickedRef.current = false;
    startTimeRef.current = performance.now();
    setPhase('active');
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
  }, [phase, disabled]);
  
  // Handle timing click
  const handleClick = useCallback(() => {
    if (phase !== 'active' || hasClickedRef.current) return;
    
    hasClickedRef.current = true;
    
    // Cancel animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Calculate result based on cursor position
    let timingResult;
    
    if (cursorPosition >= perfectZone.start && cursorPosition <= perfectZone.end) {
      timingResult = TIMING_RESULTS.PERFECT;
    } else if (cursorPosition >= greenZone.start && cursorPosition <= greenZone.end) {
      timingResult = TIMING_RESULTS.GOOD;
    } else if (
      (cursorPosition >= greenZone.start - 5 && cursorPosition < greenZone.start) ||
      (cursorPosition > greenZone.end && cursorPosition <= greenZone.end + 5)
    ) {
      timingResult = TIMING_RESULTS.OKAY;
    } else {
      timingResult = TIMING_RESULTS.MISS;
    }
    
    // Haptic feedback based on result
    if ('vibrate' in navigator) {
      if (timingResult === TIMING_RESULTS.PERFECT) {
        navigator.vibrate([50, 30, 100, 30, 50]);
      } else if (timingResult === TIMING_RESULTS.MISS) {
        navigator.vibrate([200]);
      } else {
        navigator.vibrate([50]);
      }
    }
    
    setResult(timingResult);
    setPhase('result');
    
    // Calculate final power with timing bonus
    const finalPower = Math.max(1, cardPower + timingResult.bonus);
    
    // Callback after short delay
    setTimeout(() => {
      if (onComplete) {
        onComplete({
          timing: timingResult,
          finalPower,
          multiplier: timingResult.multiplier,
          cursorPosition,
          wasInZone: cursorPosition >= greenZone.start && cursorPosition <= greenZone.end,
        });
      }
    }, 800);
  }, [phase, cursorPosition, perfectZone, greenZone, cardPower, onComplete]);
  
  // Auto-start after a delay
  useEffect(() => {
    if (phase === 'ready' && !disabled) {
      const timer = setTimeout(handleStart, 500);
      return () => clearTimeout(timer);
    }
  }, [phase, disabled, handleStart]);
  
  // Class colors
  const classColors = {
    meme: '#00FF99',
    tech: '#00CCFF',
    politics: '#FF6B6B',
  };
  const accentColor = classColors[cardClass] || '#00FFFF';
  
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5" style={{ color: accentColor }} />
          <span className="font-anton text-sm text-white uppercase tracking-wider">
            Precision Attack
          </span>
        </div>
        
        {/* Sentiment indicator */}
        {sentimentModifier !== 0 && (
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
            sentimentModifier > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            <Zap className="w-3 h-3" />
            <span>{sentimentModifier > 0 ? '+' : ''}{sentimentModifier} News</span>
          </div>
        )}
      </div>
      
      {/* Skill Bar Container */}
      <motion.div
        className={`relative h-16 rounded-xl overflow-hidden cursor-pointer select-none ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        }`}
        style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
          border: `2px solid ${phase === 'active' ? accentColor : 'rgba(255,255,255,0.2)'}`,
          boxShadow: phase === 'active' ? `0 0 30px ${accentColor}40` : 'none',
        }}
        onClick={handleClick}
        whileTap={phase === 'active' ? { scale: 0.98 } : {}}
        data-testid="skill-bar"
      >
        {/* Background Grid */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              repeating-linear-gradient(90deg, transparent, transparent 10%, rgba(255,255,255,0.1) 10%, rgba(255,255,255,0.1) 10.5%)
            `,
          }}
        />
        
        {/* Miss Zone (Red) - Left */}
        <div
          className="absolute top-0 bottom-0 bg-red-900/40"
          style={{
            left: 0,
            width: `${greenZone.start}%`,
          }}
        />
        
        {/* Miss Zone (Red) - Right */}
        <div
          className="absolute top-0 bottom-0 bg-red-900/40"
          style={{
            left: `${greenZone.end}%`,
            right: 0,
          }}
        />
        
        {/* Green Zone */}
        <motion.div
          className="absolute top-0 bottom-0"
          style={{
            left: `${greenZone.start}%`,
            width: `${greenZone.end - greenZone.start}%`,
            background: `linear-gradient(180deg, rgba(0,255,153,0.4) 0%, rgba(0,255,153,0.2) 100%)`,
            borderLeft: '2px solid #00FF99',
            borderRight: '2px solid #00FF99',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        />
        
        {/* Perfect Zone (Gold/Yellow) */}
        <motion.div
          className="absolute top-0 bottom-0"
          style={{
            left: `${perfectZone.start}%`,
            width: `${perfectZone.end - perfectZone.start}%`,
            background: `linear-gradient(180deg, rgba(255,215,0,0.6) 0%, rgba(255,215,0,0.3) 100%)`,
            borderLeft: '2px solid #FFD700',
            borderRight: '2px solid #FFD700',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === 'active' ? [0.5, 1, 0.5] : 1 }}
          transition={{ duration: 0.5, repeat: phase === 'active' ? Infinity : 0 }}
        />
        
        {/* Moving Cursor */}
        <motion.div
          className="absolute top-1 bottom-1 w-2"
          style={{
            left: `calc(${cursorPosition}% - 4px)`,
            background: 'linear-gradient(180deg, #FFFFFF 0%, #CCCCCC 100%)',
            borderRadius: '4px',
            boxShadow: `
              0 0 10px rgba(255,255,255,0.8),
              0 0 20px rgba(255,255,255,0.5),
              0 0 30px ${accentColor}
            `,
          }}
          data-testid="skill-bar-cursor"
        />
        
        {/* Result Overlay */}
        <AnimatePresence>
          {result && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              style={{ 
                background: `${result.color}20`,
                backdropFilter: 'blur(4px)',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="text-center"
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: [0, 1.3, 1], rotate: [0, 5, 0] }}
              >
                <span 
                  className="font-anton text-4xl"
                  style={{ 
                    color: result.color,
                    textShadow: `0 0 20px ${result.color}, 0 0 40px ${result.color}`,
                  }}
                >
                  {result.text}
                </span>
                
                {/* Bonus indicator */}
                {result.bonus !== 0 && (
                  <motion.div
                    className="flex items-center justify-center gap-1 mt-1"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Star className="w-4 h-4" style={{ color: result.color }} />
                    <span className="font-space text-sm" style={{ color: result.color }}>
                      {result.bonus > 0 ? '+' : ''}{result.bonus} Power
                    </span>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Ready State */}
        {phase === 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-space text-white/60 text-sm">GET READY...</span>
          </div>
        )}
      </motion.div>
      
      {/* Instructions */}
      <div className="flex items-center justify-center gap-4 mt-2">
        {phase === 'active' && (
          <motion.p 
            className="font-space text-xs text-white/60"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            TAP or CLICK when cursor is in the GREEN zone!
          </motion.p>
        )}
        
        {/* Zone Legend */}
        <div className="flex items-center gap-3 text-[10px] font-space">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-500/60 border border-yellow-400" />
            <span className="text-yellow-400">PERFECT</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500/40 border border-green-400" />
            <span className="text-green-400">GOOD</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500/40 border border-red-400" />
            <span className="text-red-400">MISS</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillBar;
